<template>
  <!--
    Formulario "Generar certificación" (Solaring).
    Formulario autocontenido SIN el chrome del modal: el prefijo `Form` indica que
    puede embeberse en cualquier contenedor (un modal externo, un panel, etc.).
    Compartido por certificación mecánica, eléctrica y obra civil.
    Emite 'created' con el payload y 'close' para que el contenedor cierre.
  -->
  <div>
    <div class="gen-cert-form" :class="{ 'pointer-events-none': isCreating }">
      <!-- Overlay de generación: cubre el cuerpo con spinner + paso actual (estado unificado) -->
      <b-loading :active="isCreating" :is-full-page="false">
        <div class="gen-cert-loader">
          <span class="gen-cert-loader__ring"></span>
          <p class="gen-cert-loader__step">{{ $t(progressStepKey) }}</p>
          <p class="gen-cert-loader__hint">{{ $t('please_wait') }}</p>
        </div>
      </b-loading>

      <!-- Fila 1: Mes de certificación (solo primera columna) -->
      <div class="grid grid-cols-3 gap-4">
        <b-field
          :label="$t('certification_month')"
          class="gen-cert-dialog__field"
          :type="fieldErrors.month ? 'is-danger' : null"
          :message="fieldErrors.month ? $t('field_required') : null"
        >
          <b-datepicker
            v-model="form.month"
            type="month"
            size="is-small"
            icon="calendar"
            icon-pack="fas"
            :placeholder="$t('select')"
            :month-names="$t('months_names')"
            :day-names="$t('days_names')"
            :first-day-of-week="parseInt($t('first_day_of_week'))"
            expanded
            @input="_ClearFieldError('month')"
          />
        </b-field>
      </div>

      <!-- Fila 2: Contratista | Tipo de certificación | Idioma del documento -->
      <div class="grid grid-cols-3 gap-4">
        <b-field
          :label="$t('contractor')"
          class="gen-cert-dialog__field"
          :type="fieldErrors.contractor ? 'is-danger' : null"
          :message="fieldErrors.contractor ? $t('field_required') : null"
        >
          <multiselect
            v-model="selectedContractor"
            :options="contractors"
            label="name"
            track-by="id"
            :show-labels="false"
            :searchable="true"
            :allow-empty="true"
            :placeholder="$t('select')"
            :loading="isLoadingOrders"
            :class="{ 'multiselect--danger': fieldErrors.contractor }"
            @input="_OnContractorChange"
          >
            <template slot="noResult">{{ $t('no_elements_found') }}</template>
            <template slot="noOptions">{{ $t('no_options_available') }}</template>
          </multiselect>
        </b-field>

        <b-field :label="$t('certification_type')" class="gen-cert-dialog__field">
          <b-input :value="certifiesByLabel" size="is-small" disabled expanded />
        </b-field>

        <b-field :label="$t('document_language')" class="gen-cert-dialog__field">
          <b-input :value="documentLanguageLabel" size="is-small" disabled expanded />
        </b-field>
      </div>

      <!-- Fila 3: Orden de pedido | ¿Última certificación? -->
      <div class="grid grid-cols-2 gap-4">
        <b-field
          :label="$t('work_order')"
          class="gen-cert-dialog__field"
          :type="fieldErrors.workOrder ? 'is-danger' : null"
          :message="fieldErrors.workOrder ? $t('field_required') : null"
        >
          <multiselect
            v-model="selectedOrderModel"
            :options="filteredOrders"
            label="code"
            track-by="id"
            :show-labels="false"
            :searchable="true"
            :allow-empty="true"
            :placeholder="$t('select')"
            :loading="isLoadingOrders"
            :disabled="!selectedContractor || isCreating"
            :class="{ 'multiselect--danger': fieldErrors.workOrder }"
            @input="_ClearFieldError('workOrder')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <span class="gen-cert-order__opt">
                <span class="gen-cert-order__code">{{ option.code }}</span>
                <span v-if="option.is_extension" class="gen-cert-ext-tag">{{ $t('work_order_extension') }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <span class="gen-cert-order__opt">
                <span class="gen-cert-order__code">{{ option.code }}</span>
                <span v-if="option.is_extension" class="gen-cert-ext-tag">{{ $t('work_order_extension') }}</span>
              </span>
            </template>
            <template slot="noResult">{{ $t('no_elements_found') }}</template>
            <template slot="noOptions">{{ $t('no_options_available') }}</template>
          </multiselect>
        </b-field>

        <b-field :label="$t('is_last_certification')" class="gen-cert-dialog__field">
          <b-select v-model="form.isLast" expanded size="is-small">
            <option :value="false">{{ $t('no') }}</option>
            <option :value="true">{{ $t('yes') }}</option>
          </b-select>
        </b-field>
      </div>

      <!-- Aviso: la carga BOQ del pedido aún no está validada → no se puede certificar. -->
      <div v-if="orderNotValidated" class="epc-alert epc-alert--warning">
        <div class="epc-alert__text">{{ $t('certification_order_boq_not_validated') }}</div>
      </div>

      <!-- Fila 4: Contracargos | BOQ sin vincular (dos columnas) -->
      <div class="grid grid-cols-2 gap-4">
      <!-- Apartado Contracargo -->
      <div class="border border-gray-200 rounded-lg bg-white px-3 py-2 flex flex-col max-h-80">
        <div class="flex items-center justify-between gap-2 mb-2">
          <b-field :label="$t('chargebacks')"></b-field>
          <button
            v-if="!showChargebackForm"
            type="button"
            class="gen-cert-dialog__btn gen-cert-dialog__btn--confirm gen-cert-dialog__btn--sm inline-flex items-center gap-1"
            :disabled="orderNotValidated"
            @click="openChargebackForm"
            size="is-small"
          >
            <b-icon pack="fas" icon="plus" size="is-small" />
            <span>{{ $t('add') }}</span>
          </button>
        </div>

        <!-- Con el formulario abierto: overflow visible para que el desplegable del multiselect
             no lo recorte el box; con la lista: scroll interno. -->
        <div class="flex-auto min-h-0" :class="showChargebackForm ? 'overflow-visible' : 'overflow-y-auto'">
        <!-- Formulario de contracargo: se muestra al pulsar "Añadir" (oculta la tabla) -->
        <template v-if="showChargebackForm">
          <fieldset class="gen-cert-dialog__fieldset">
            <legend>{{ editingIndex !== null ? $t('save') : $t('add_chargeback') }}</legend>
            <b-field
              :label="$t('line_item')"
              class="gen-cert-dialog__field"
              :type="chargebackErrors.backcharge ? 'is-danger' : null"
              :message="chargebackErrors.backcharge ? $t('field_required') : null"
            >
              <multiselect
                v-model="chargebackDraft.backcharge"
                :options="backcharges"
                :custom-label="backchargeLabel"
                track-by="id"
                :show-labels="false"
                :searchable="true"
                :allow-empty="true"
                :placeholder="$t('select')"
                :loading="isLoadingBackcharges"
                :class="{ 'multiselect--danger': chargebackErrors.backcharge }"
                @input="_ClearChargebackError('backcharge')"
              >
                <template slot="noResult">{{ $t('no_elements_found') }}</template>
                <template slot="noOptions">{{ $t('no_options_available') }}</template>
              </multiselect>
            </b-field>

            <div class="flex items-end gap-2 mb-2">
              <b-field
                :label="$t('quantity')"
                class="gen-cert-dialog__field gen-cert-dialog__field--half"
                :type="chargebackErrors.quantity ? 'is-danger' : null"
                :message="chargebackErrors.quantity ? $t('field_required') : null"
              >
                <b-input v-model="chargebackDraft.quantity" type="number" step="any" size="is-small" @input="_ClearChargebackError('quantity')" />
              </b-field>
              <b-field
                :label="$t('price')"
                class="gen-cert-dialog__field gen-cert-dialog__field--half"
                :type="chargebackErrors.price ? 'is-danger' : null"
                :message="_priceErrorMessage"
              >
                <b-input v-model="chargebackDraft.price" type="text" inputmode="decimal" size="is-small" @keydown="_BlockNegativeKey" @input="_OnPriceInput" />
              </b-field>
            </div>

            <b-field :label="$t('observations')" class="gen-cert-dialog__field">
              <b-input
                v-model="chargebackDraft.observation"
                type="textarea"
                size="is-small"
                rows="1"
                :placeholder="$t('observations')"
              />
            </b-field>

            <div class="flex justify-between gap-2 mt-1">
              <button
                type="button"
                class="gen-cert-dialog__btn gen-cert-dialog__btn--cancel gen-cert-dialog__btn--sm inline-flex items-center justify-center gap-1"
                @click="cancelChargebackForm"
              >
                <b-icon pack="fas" icon="times" size="is-small" />
                <span>{{ $t('cancel') }}</span>
              </button>
              <button
                type="button"
                class="gen-cert-dialog__btn gen-cert-dialog__btn--confirm gen-cert-dialog__btn--sm inline-flex items-center justify-center gap-1"
                @click="addChargeback"
              >
                <b-icon pack="fas" :icon="editingIndex !== null ? 'save' : 'plus'" size="is-small" />
                <span>{{ editingIndex !== null ? $t('save') : $t('add_chargeback') }}</span>
              </button>
            </div>
          </fieldset>

        </template>

        <!-- Lista de contracargos + botón "Añadir" (por defecto) -->
        <template v-else>
          <table class="gen-cert-dialog__table">
            <thead>
              <tr>
                <th class="sticky top-0 z-10">{{ $t('line_item') }}</th>
                <th class="is-right sticky top-0 z-10">{{ $t('quantity') }}</th>
                <th class="is-right sticky top-0 z-10">{{ $t('price') }}</th>
                <th class="is-center sticky top-0 z-10">{{ $t('actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="chargebacks.length === 0">
                <td colspan="4" class="is-empty">{{ $t('no_results') }}</td>
              </tr>
              <tr v-for="(cb, index) in chargebacks" :key="'cb_' + index">
                <td>
                  {{ cb.measurement }}
                  <div v-if="cb.observation" class="gen-cert-dialog__cb-obs">{{ cb.observation }}</div>
                </td>
                <td class="is-right">{{ cb.quantity }}</td>
                <td class="is-right">{{ cb.price }}</td>
                <td class="is-center">
                  <button type="button" class="action-icon-btn" @click="editChargeback(index)">
                    <b-icon pack="fas" icon="pen" size="is-small" />
                  </button>
                  <button type="button" class="action-icon-btn action-icon-btn--delete" @click="deleteChargeback(index)">
                    <b-icon pack="fas" icon="trash" size="is-small" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
        </div>
      </div>

      <!-- Líneas de BOQs a certificar (BOQ civil sin vincular) -->
      <div class="border border-gray-200 rounded-lg bg-white px-3 py-2 flex flex-col max-h-80">
        <div class="flex items-center justify-between gap-2 mb-2">
          <b-field :label="$t('boq_lines_to_certify')"></b-field>
          <button
            v-if="!showBoqForm"
            type="button"
            class="gen-cert-dialog__btn gen-cert-dialog__btn--confirm gen-cert-dialog__btn--sm inline-flex items-center gap-1"
            :disabled="orderNotValidated"
            @click="openBoqForm"
          >
            <b-icon pack="fas" icon="plus" size="is-small" />
            <span>{{ $t('add') }}</span>
          </button>
        </div>

        <!-- Con el formulario abierto: overflow visible para que el desplegable del multiselect
             no lo recorte el box; con la lista: scroll interno. -->
        <div class="flex-auto min-h-0" :class="showBoqForm ? 'overflow-visible' : 'overflow-y-auto'">
          <!-- Formulario de línea BOQ: se muestra al pulsar "Añadir" (oculta la tabla) -->
          <template v-if="showBoqForm">
            <fieldset class="gen-cert-dialog__fieldset">
              <legend>{{ editingBoqIndex !== null ? $t('save') : $t('add_boq_line') }}</legend>

              <!-- Fila 1: selección de la línea BOQ civil (sin vincular) -->
              <b-field
                :label="$t('line_item')"
                class="gen-cert-dialog__field"
                :type="boqErrors.boq ? 'is-danger' : null"
                :message="boqErrors.boq ? $t('field_required') : null"
              >
                <multiselect
                  v-model="boqDraft.boq"
                  :options="civilBoqs"
                  :custom-label="boqOptionLabel"
                  track-by="id"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="true"
                  :placeholder="$t('select')"
                  :loading="isLoadingCivilBoqs"
                  :class="{ 'multiselect--danger': boqErrors.boq }"
                  @input="_OnSelectBoqLine"
                >
                  <template slot="singleLabel" slot-scope="{ option }">
                    <span class="inline-flex items-start gap-1">
                      <span v-if="option.line_pedido" class="gen-cert-boq__tag">{{ option.line_pedido }}</span>
                      <span class="gen-cert-boq__name">{{ option.description || option.code || '—' }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="{ option }">
                    <div class="gen-cert-boq__opt">
                      <div class="flex items-center gap-1 mb-1">
                        <span v-if="option.line_pedido" class="gen-cert-boq__tag">{{ option.line_pedido }}</span>
                        <span v-if="option.code" class="gen-cert-boq__code">{{ option.code }}</span>
                      </div>
                      <div class="gen-cert-boq__name">{{ option.description || '—' }}</div>
                      <div class="gen-cert-boq__meta">
                        <span>{{ _FormatQty(option.quantity) }} {{ option.unit || '' }}</span>
                        <span>{{ _FormatPrice(option.price_unit) }}</span>
                      </div>
                    </div>
                  </template>
                  <template slot="noResult">{{ $t('no_elements_found') }}</template>
                  <template slot="noOptions">{{ $t('no_options_available') }}</template>
                </multiselect>
              </b-field>

              <!-- Referencia de la línea seleccionada (cantidad + unidad), fuera de input -->
              <div v-if="boqDraft.boq" class="gen-cert-boq__ref text-gray-500 mb-2">
                {{ $t('quantity') }}:
                <strong class="text-gray-700">{{ _FormatQty(boqDraft.boq.quantity) }} {{ boqDraft.boq.unit || '' }}</strong>
              </div>

              <!-- Fila 2: cantidad a ejecutar | ejecutada | pendiente | precio (bloqueado) -->
              <div class="grid grid-cols-4 gap-2">
                <b-field
                  :label="$t('quantity_to_execute')"
                  class="gen-cert-dialog__field"
                  :type="boqErrors.toExecute ? 'is-danger' : null"
                  :message="_boqToExecuteMessage"
                >
                  <b-input v-model="boqDraft.toExecute" type="number" step="any" min="0" size="is-small" @input="_ClearBoqError('toExecute')" />
                </b-field>
                <b-field :label="$t('executed_quantity')" class="gen-cert-dialog__field">
                  <b-input :value="boqDraft.executed" size="is-small" disabled />
                </b-field>
                <b-field :label="$t('pending_quantity')" class="gen-cert-dialog__field">
                  <b-input :value="boqPendingDraft" size="is-small" disabled />
                </b-field>
                <b-field :label="$t('price')" class="gen-cert-dialog__field">
                  <b-input :value="boqDraft.price" size="is-small" disabled />
                </b-field>
              </div>

              <b-field :label="$t('observations')" class="gen-cert-dialog__field">
                <b-input v-model="boqDraft.observation" type="textarea" size="is-small" rows="1" :placeholder="$t('observations')" />
              </b-field>

              <div class="flex justify-between gap-2 mt-1">
                <button
                  type="button"
                  class="gen-cert-dialog__btn gen-cert-dialog__btn--cancel gen-cert-dialog__btn--sm inline-flex items-center justify-center gap-1"
                  @click="cancelBoqForm"
                >
                  <b-icon pack="fas" icon="times" size="is-small" />
                  <span>{{ $t('cancel') }}</span>
                </button>
                <button
                  type="button"
                  class="gen-cert-dialog__btn gen-cert-dialog__btn--confirm gen-cert-dialog__btn--sm inline-flex items-center justify-center gap-1"
                  @click="addBoqLine"
                >
                  <b-icon pack="fas" :icon="editingBoqIndex !== null ? 'save' : 'plus'" size="is-small" />
                  <span>{{ editingBoqIndex !== null ? $t('save') : $t('add_boq_line') }}</span>
                </button>
              </div>
            </fieldset>
          </template>

          <!-- Lista de líneas BOQ añadidas (por defecto) -->
          <template v-else>
            <table class="gen-cert-dialog__table">
              <thead>
                <tr>
                  <th class="sticky top-0 z-10">{{ $t('line_item') }}</th>
                  <th class="is-right sticky top-0 z-10">{{ $t('quantity_to_execute') }}</th>
                  <th class="is-right sticky top-0 z-10">{{ $t('price') }}</th>
                  <th class="is-center sticky top-0 z-10">{{ $t('actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="boqLines.length === 0">
                  <td colspan="4" class="is-empty">{{ $t('no_results') }}</td>
                </tr>
                <tr v-for="(bl, index) in boqLines" :key="'bl_' + index">
                  <td>
                    <span class="gen-cert-boq__name">{{ bl.measurement }}</span>
                    <div v-if="bl.observation" class="gen-cert-dialog__cb-obs">{{ bl.observation }}</div>
                  </td>
                  <td class="is-right">{{ bl.toExecute }}</td>
                  <td class="is-right">{{ bl.price }}</td>
                  <td class="is-center">
                    <button type="button" class="action-icon-btn" @click="editBoqLine(index)">
                      <b-icon pack="fas" icon="pen" size="is-small" />
                    </button>
                    <button type="button" class="action-icon-btn action-icon-btn--delete" @click="deleteBoqLine(index)">
                      <b-icon pack="fas" icon="trash" size="is-small" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
      </div>

      <!-- Fila 5: Observaciones (ancho completo) -->
      <b-field :label="$t('observations')" class="gen-cert-dialog__field mt-1">
        <b-input v-model="form.observations" type="textarea" size="is-small" rows="2" />
      </b-field>
    </div>

    <!-- Footer inline (sin modal): el contenedor externo aporta el chrome del diálogo. -->
    <div class="flex justify-end gap-2 mt-4">
      <b-button size="is-small" :disabled="isCreating" @click="$emit('close')">
        {{ $t('cancel') }}
      </b-button>
      <!-- Crear se bloquea mientras el formulario de contracargo/BOQ esté abierto (hay que cerrarlo antes). -->
      <b-tooltip
        :active="(showChargebackForm || showBoqForm) && !isCreating"
        :label="$t('finish_chargeback_first')"
        position="is-top"
        type="is-dark"
      >
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="check"
          icon-pack="fas"
          :loading="isCreating"
          :disabled="isCreating || showChargebackForm || showBoqForm || orderNotValidated"
          @click="create"
        >
          {{ isCreating ? $t(progressStepKey) : $t('create') }}
        </b-button>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import Multiselect from 'vue-multiselect'

export default {
  name: 'CertificationFormGenerateSolaring',

  components: {
    Multiselect,
  },

  props: {
    projectId: { type: Number, default: null },
    // Entidad que se está certificando (PartnerProcessEntity int):
    // 1=obra civil, 2=montaje mecánico, 3=instalación eléctrica.
    entityType: { type: Number, default: null },
  },

  data: () => ({
    // Pedidos del proyecto (cargados del endpoint), cada uno con partner + certifies_by.
    orders: [],
    isLoadingOrders: false,
    isCreating: false,
    selectedContractor: null,
    form: {
      orders: [],
      month: null,
      observations: '',
      isLast: false,
    },
    // Contracargos del proyecto (para el multiselect).
    backcharges: [],
    isLoadingBackcharges: false,
    // Linea de contracargo en edicion (inputs de arriba).
    chargebackDraft: {
      backcharge: null,
      quantity: null,
      price: null,
      observation: '',
    },
    editingIndex: null,
    showChargebackForm: false, // false = tabla + botón añadir; true = formulario
    chargebackErrors: { backcharge: false, quantity: false, price: false },
    chargebacks: [],
    // --- Líneas de BOQ a certificar (BOQ civil sin vincular) ---
    civilBoqs: [],              // opciones del multiselect (líneas boq_details tipo civil)
    isLoadingCivilBoqs: false,
    boqDraft: { boq: null, toExecute: null, executed: 0, price: null, observation: '' },
    editingBoqIndex: null,
    showBoqForm: false,         // false = tabla + botón añadir; true = formulario
    boqErrors: { boq: false, toExecute: false },
    boqLines: [],
    fieldErrors: {
      month: false,
      contractor: false,
      workOrder: false,
    },
    progressStepKey: 'cert_progress_saving',
    progressTimers: [],
  }),

  computed: {
    // Mecánico / eléctrico: el pedido debe tener flujo de validación asignado.
    requiresValidationFlow() {
      return this.entityType === 2 || this.entityType === 3
    },
    // Mensaje de error del campo precio del contracargo: distingue "requerido"
    // (vacío) de "no puede ser negativo". Devuelve null si no hay error.
    _priceErrorMessage() {
      if (!this.chargebackErrors.price) return null
      const p = this.chargebackDraft.price
      if (p !== null && p !== '' && !isNaN(Number(p)) && Number(p) < 0) {
        return this.$t('price_cannot_be_negative')
      }
      return this.$t('field_required')
    },
    // Contratistas únicos derivados de los pedidos (con búsqueda).
    contractors() {
      const map = new Map()
      this.orders.forEach((order) => {
        if (!order.partner_id || map.has(order.partner_id)) {
          return
        }
        map.set(order.partner_id, {
          id: order.partner_id,
          name: order.partner_name || `#${order.partner_id}`,
          certifies_by: order.certifies_by,
          documentLanguage: order.document_language || null,
        })
      })
      return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)))
    },
    // Pedidos asignados a la contratista seleccionada.
    filteredOrders() {
      if (!this.selectedContractor) {
        return []
      }
      return this.orders.filter((order) => order.partner_id === this.selectedContractor.id)
    },
    // Modelo del multiselect (objeto unico). Se mantiene form.orders como array
    // para no romper la logica de order_ids / order_code / selectedOrder.
    selectedOrderModel: {
      get() {
        return this.form.orders.length ? this.form.orders[0] : null
      },
      set(value) {
        this.form.orders = value ? [value] : []
      },
    },
    // Pedido de referencia (el primero seleccionado).
    selectedOrder() {
      return this.form.orders.length ? this.form.orders[0] : null
    },
    // El pedido tiene una carga BOQ que aún no está validada (boq_validated === false).
    // null = no aplica (mec/elec o sin carga BOQ). Bloquea la certificación.
    orderNotValidated() {
      return !!this.selectedOrder && this.selectedOrder.boq_validated === false
    },
    certifiesByLabel() {
      const cb = this.selectedContractor
        ? this.selectedContractor.certifies_by
        : (this.selectedOrder ? this.selectedOrder.certifies_by : null)
      if (cb === 1) return this.$t('production')
      if (cb === 2) return this.$t('quality')
      return ''
    },
    // Idioma configurado del documento.
    // Sin configurar (null) → el PDF sale en el idioma del usuario logueado.
    documentLanguageLabel() {
      const lang = this.selectedContractor
        ? this.selectedContractor.documentLanguage
        : (this.selectedOrder ? this.selectedOrder.document_language : null)
      const names = { es: 'spanish', en: 'english', pt: 'portuguese', it: 'italian' }
      if (lang && names[lang]) return this.$t(names[lang])
      return this.$t('document_language_user_default')
    },
    // Cantidad pendiente del borrador BOQ = total de la línea − ejecutada − a ejecutar (≥ 0).
    boqPendingDraft() {
      const total = this.boqDraft.boq ? Number(this.boqDraft.boq.quantity) : 0
      const executed = Number(this.boqDraft.executed) || 0
      const toExec = Number(this.boqDraft.toExecute) || 0
      const pending = total - executed - toExec
      return this._FormatQty(Number.isFinite(pending) ? Math.max(0, pending) : 0)
    },
    // Cantidad disponible a ejecutar = total de la línea − ya ejecutada.
    _boqAvailable() {
      const total = this.boqDraft.boq ? Number(this.boqDraft.boq.quantity) : 0
      const executed = Number(this.boqDraft.executed) || 0
      return Math.max(0, total - executed)
    },
    // Mensaje del campo "cantidad a ejecutar": distingue requerido de "supera la pendiente".
    _boqToExecuteMessage() {
      if (!this.boqErrors.toExecute) return null
      const toExec = Number(this.boqDraft.toExecute)
      if (this.boqDraft.boq && !isNaN(toExec) && toExec > this._boqAvailable) {
        return this.$t('quantity_exceeds_pending')
      }
      return this.$t('field_required')
    },
  },

  watch: {
    // Al cambiar la orden de pedido, recarga las líneas BOQ (filtradas por ese pedido) y
    // limpia la selección del borrador.
    selectedOrder() {
      this.boqDraft.boq = null
      this.loadCivilBoqs()
    },
  },

  created() {
    this.loadOrders()
    this.loadBackcharges()
    this.loadCivilBoqs()
  },

  beforeDestroy() {
    this._ClearProgressTimers()
  },

  methods: {
    _ClearFieldError(field) {
      if (this.fieldErrors[field]) {
        this.fieldErrors[field] = false
      }
    },

    _ResetFieldErrors() {
      this.fieldErrors = {
        month: false,
        contractor: false,
        workOrder: false,
      }
    },

    _ClearProgressTimers() {
      (this.progressTimers || []).forEach((id) => clearTimeout(id))
      this.progressTimers = []
    },

    _StartProgressSteps() {
      this._ClearProgressTimers()
      this.progressStepKey = 'cert_progress_saving'
      this.progressTimers.push(setTimeout(() => {
        if (this.isCreating) this.progressStepKey = 'cert_progress_creating_flow'
      }, 900))
      this.progressTimers.push(setTimeout(() => {
        if (this.isCreating) this.progressStepKey = 'cert_progress_finalizing'
      }, 2800))
    },

    _ValidateCreateForm() {
      this._ResetFieldErrors()
      let valid = true

      if (!this.form.month) {
        this.fieldErrors.month = true
        valid = false
      }
      if (!this.selectedContractor) {
        this.fieldErrors.contractor = true
        valid = false
      }
      if (!this.form.orders.length) {
        this.fieldErrors.workOrder = true
        valid = false
      }

      if (!valid) {
        this.$notify.warning('required_fields')
        return false
      }

      // Mecánico/eléctrico: sin flujo de validación en el pedido no se puede crear.
      if (this.requiresValidationFlow) {
        const order = this.selectedOrder
        if (!order || !order.has_flow) {
          this.fieldErrors.workOrder = true
          this.$notify.error('order_without_flow')
          return false
        }
      }

      // La carga BOQ del pedido debe estar validada antes de certificar.
      if (this.orderNotValidated) {
        this.fieldErrors.workOrder = true
        this.$notify.error('certification_order_boq_not_validated')
        return false
      }

      return true
    },

    _NotifyApiError(error) {
      const status = error && error.response && error.response.status
      const data = error && error.response && error.response.data
      // 403: falta de permiso para generar (backend exige rol admin o certificador).
      // El backend responde "This action is unauthorized." (sin traducción) → mostramos
      // un mensaje claro y localizado en su lugar.
      if (status === 403) {
        this.$notify.error('certification_generate_not_authorized')
        return
      }
      if (data && data.message) {
        const translated = this.$t(data.message)
        this.$notify.error(translated !== data.message ? translated : data.message)
        return
      }
      if (data && data.errors) {
        const firstKey = Object.keys(data.errors)[0]
        const firstMsg = firstKey && data.errors[firstKey] && data.errors[firstKey][0]
        if (firstMsg) {
          this.$notify.error(firstMsg)
          return
        }
      }
      this.$notify.error('error')
    },

    _OnContractorChange() {
      // Al cambiar contratista se limpia el pedido; el tipo se deriva de la contratista.
      this.form.orders = []
      this._ClearFieldError('contractor')
      this._ClearFieldError('workOrder')
    },

    // Etiqueta del contracargo en el multiselect: "line_item — descripción".
    backchargeLabel(backcharge) {
      if (!backcharge) return ''
      const lineItem = backcharge.line_item || ''
      const description = backcharge.description || ''
      if (lineItem && description) return `${lineItem} — ${description}`
      return lineItem || description || `#${backcharge.id}`
    },

    // Carga los contracargos del proyecto.
    async loadBackcharges() {
      if (!this.projectId) {
        return
      }
      this.isLoadingBackcharges = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/backcharges`)
        if (data && data.success) {
          this.backcharges = data.data || []
        }
      } catch (e) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingBackcharges = false
      }
    },

    // Carga los pedidos del proyecto (con contrata + certifies_by para la entidad).
    async loadOrders() {
      if (!this.projectId || !this.entityType) {
        return
      }
      this.isLoadingOrders = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/certification-orders`,
          { params: { entity_type: this.entityType } }
        )
        if (data && data.success) {
          this.orders = data.data || []
        }
      } catch (e) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingOrders = false
      }
    },
    _ResetChargebackErrors() {
      this.chargebackErrors = { backcharge: false, quantity: false, price: false }
    },

    _ClearChargebackError(field) {
      if (this.chargebackErrors[field]) {
        this.chargebackErrors[field] = false
      }
    },

    // Abre el formulario de contracargo (oculta la tabla) en modo "nuevo".
    openChargebackForm() {
      this.editingIndex = null
      this.resetChargebackDraft()
      this._ResetChargebackErrors()
      this.showChargebackForm = true
    },

    // Cancela: oculta el formulario, vuelve a la tabla y resetea los campos.
    cancelChargebackForm() {
      this.editingIndex = null
      this.resetChargebackDraft()
      this._ResetChargebackErrors()
      this.showChargebackForm = false
    },

    // Impide teclear el signo menos en el input de precio (evita el "flash";
    // igual _OnPriceInput lo sanitiza como backstop ante pegado).
    _BlockNegativeKey(e) {
      if (e.key === '-') {
        e.preventDefault()
      }
    },

    // Sanitiza un valor decimal de texto (usado por el precio): solo dígitos,
    // UN separador decimal y hasta 2 decimales, sin negativos. Admite coma o
    // punto y normaliza a punto (formato que espera el backend).
    _SanitizeDecimal(value) {
      let v = String(value == null ? '' : value)
      v = v.replace(/[^\d.,]/g, '')   // fuera todo lo que no sea dígito, coma o punto
      v = v.replace(/,/g, '.')        // coma → punto (separador unificado)
      const firstDot = v.indexOf('.')
      if (firstDot !== -1) {
        // dejar solo el primer punto
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '')
        // máximo 2 decimales
        const parts = v.split('.')
        v = parts[0] + '.' + parts[1].slice(0, 2)
      }
      return v
    },

    // @input del precio: sanitiza y limpia el error.
    _OnPriceInput(value) {
      this.chargebackDraft.price = this._SanitizeDecimal(value)
      this._ClearChargebackError('price')
    },

    // Añade (o actualiza si se estaba editando) una linea de contracargo desde los inputs.
    // Marca en rojo los campos que falten (feedback claro, en vez de solo un toast genérico).
    addChargeback() {
      const draft = this.chargebackDraft
      const errors = {
        backcharge: !draft.backcharge,
        quantity: draft.quantity === null || draft.quantity === '' || isNaN(Number(draft.quantity)),
        price: draft.price === null || draft.price === '' || isNaN(Number(draft.price)),
      }
      this.chargebackErrors = errors
      if (errors.backcharge || errors.quantity || errors.price) {
        this.$notify.warning('required_fields')
        return
      }
      // El precio no puede ser negativo (backstop; el input además sanitiza
      // en _OnPriceInput —sin negativos, 2 decimales— y bloquea el signo menos).
      if (Number(draft.price) < 0) {
        this.chargebackErrors.price = true
        this.$notify.warning('price_cannot_be_negative')
        return
      }
      const row = {
        backchargeId: draft.backcharge.id,
        measurement: this.backchargeLabel(draft.backcharge),
        quantity: Number(draft.quantity),
        price: Number(draft.price),
        observation: (draft.observation || '').trim(),
      }
      if (this.editingIndex !== null) {
        this.chargebacks.splice(this.editingIndex, 1, row)
        this.editingIndex = null
      } else {
        this.chargebacks.push(row)
      }
      this.resetChargebackDraft()
      this._ResetChargebackErrors()
      // Tras añadir/guardar, volvemos a la tabla.
      this.showChargebackForm = false
    },

    editChargeback(index) {
      const cb = this.chargebacks[index]
      this.chargebackDraft = {
        backcharge: this.backcharges.find(b => b.id === cb.backchargeId) || null,
        quantity: cb.quantity,
        price: cb.price,
        observation: cb.observation || '',
      }
      this._ResetChargebackErrors()
      this.editingIndex = index
      this.showChargebackForm = true // editar abre el formulario
    },

    deleteChargeback(index) {
      this.chargebacks.splice(index, 1)
      if (this.editingIndex === index) {
        this.editingIndex = null
        this.resetChargebackDraft()
      }
    },

    resetChargebackDraft() {
      this.chargebackDraft = { backcharge: null, quantity: null, price: null, observation: '' }
    },

    // ---- Líneas de BOQ a certificar (BOQ civil sin vincular) -----------------

    _FormatQty(v) {
      if (v === null || v === undefined || v === '' || isNaN(Number(v))) return ''
      return Number(v).toLocaleString('es-ES', { maximumFractionDigits: 4 })
    },
    _FormatPrice(v) {
      if (v === null || v === undefined || v === '' || isNaN(Number(v))) return ''
      return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    },

    // Etiqueta de búsqueda/fallback del multiselect (item + código + nombre).
    boqOptionLabel(boq) {
      if (!boq) return ''
      return [boq.line_pedido, boq.code, boq.description].filter(Boolean).join(' · ')
    },

    // Carga las líneas BOQ civiles a certificar: SOLO las del pedido seleccionado y marcadas
    // "sin mediciones" (no_measurement_link) → se certifican manualmente aquí.
    async loadCivilBoqs() {
      const orderId = this.selectedOrder ? this.selectedOrder.id : null
      if (!this.projectId || !orderId) {
        this.civilBoqs = []
        return
      }
      this.isLoadingCivilBoqs = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-details`,
          { params: { type: 'civil', work_order_id: orderId, no_measurement_link: 1 } }
        )
        if (data && data.success) {
          this.civilBoqs = data.boqs || []
        }
      } catch (e) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingCivilBoqs = false
      }
    },

    _ResetBoqErrors() {
      this.boqErrors = { boq: false, toExecute: false }
    },
    _ClearBoqError(field) {
      if (this.boqErrors[field]) {
        this.boqErrors[field] = false
      }
    },

    // Al seleccionar una línea BOQ: precio bloqueado desde la línea; ejecutada = ya certificado
    // a mano (certified_accumulated del backend) → el pendiente y el tope salen correctos.
    _OnSelectBoqLine(option) {
      this._ClearBoqError('boq')
      if (option) {
        this.boqDraft.price = this._FormatPrice(option.price_unit)
        this.boqDraft.executed = Number(option.certified_accumulated) || 0
      } else {
        this.boqDraft.price = null
        this.boqDraft.executed = 0
      }
    },

    openBoqForm() {
      this.editingBoqIndex = null
      this.resetBoqDraft()
      this._ResetBoqErrors()
      this.showBoqForm = true
    },
    cancelBoqForm() {
      this.editingBoqIndex = null
      this.resetBoqDraft()
      this._ResetBoqErrors()
      this.showBoqForm = false
    },
    resetBoqDraft() {
      this.boqDraft = { boq: null, toExecute: null, executed: 0, price: null, observation: '' }
    },

    addBoqLine() {
      const draft = this.boqDraft
      const errors = {
        boq: !draft.boq,
        toExecute: draft.toExecute === null || draft.toExecute === '' || isNaN(Number(draft.toExecute)) || Number(draft.toExecute) <= 0,
      }
      this.boqErrors = errors
      if (errors.boq || errors.toExecute) {
        this.$notify.warning('required_fields')
        return
      }
      // No se puede ejecutar más de lo pendiente (total de la línea − ya ejecutada).
      if (Number(draft.toExecute) > this._boqAvailable) {
        this.boqErrors.toExecute = true
        this.$notify.warning('quantity_exceeds_pending')
        return
      }
      const row = {
        boqId: draft.boq.id,
        measurement: this.boqOptionLabel(draft.boq),
        toExecute: Number(draft.toExecute),
        executed: Number(draft.executed) || 0,
        price: draft.price,
        observation: (draft.observation || '').trim(),
      }
      if (this.editingBoqIndex !== null) {
        this.boqLines.splice(this.editingBoqIndex, 1, row)
        this.editingBoqIndex = null
      } else {
        this.boqLines.push(row)
      }
      this.resetBoqDraft()
      this._ResetBoqErrors()
      this.showBoqForm = false
    },
    editBoqLine(index) {
      const bl = this.boqLines[index]
      this.boqDraft = {
        boq: this.civilBoqs.find(b => b.id === bl.boqId) || null,
        toExecute: bl.toExecute,
        executed: bl.executed || 0,
        price: bl.price,
        observation: bl.observation || '',
      }
      this._ResetBoqErrors()
      this.editingBoqIndex = index
      this.showBoqForm = true
    },
    deleteBoqLine(index) {
      this.boqLines.splice(index, 1)
      if (this.editingBoqIndex === index) {
        this.editingBoqIndex = null
        this.resetBoqDraft()
      }
    },

    async create() {
      if (!this._ValidateCreateForm()) {
        return
      }
      if (!this.projectId || !this.entityType) {
        this.$notify.error('error')
        return
      }

      const order0 = this.selectedOrder
      const amount = this.chargebacks.reduce((sum, cb) => sum + (cb.quantity * cb.price), 0)

      // Payload para el backend: guarda en certification_validation_flows + intermedia.
      // El flujo y la contrata se derivan del pedido; las fechas, del mes.
      const apiPayload = {
        work_order_id: order0.id,
        entity_type: this.entityType,
        month: moment(this.form.month).format('YYYY-MM'),
        observations: this.form.observations || null,
        is_last: !!this.form.isLast,
        backcharges: this.chargebacks.map(cb => ({
          backcharge_id: cb.backchargeId,
          quantity: cb.quantity,
          price: cb.price,
          observation: cb.observation || null,
        })),
        // Líneas BOQ manuales ("sin medición") a certificar a mano (el precio se toma en backend).
        boq_lines: this.boqLines.map(bl => ({
          boq_detail_id: bl.boqId,
          quantity: bl.toExecute,
          observation: bl.observation || null,
        })),
      }

      this.isCreating = true
      this._StartProgressSteps()
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/pictogram-certifications`,
          apiPayload
        )
        if (!data || !data.success) {
          const msg = data && data.message
          this.$notify.error(msg ? (this.$t(msg) !== msg ? this.$t(msg) : msg) : 'error')
          return
        }

        this.progressStepKey = 'cert_progress_done'
        this.$notify.success('success')

        // Fila optimista para la tabla del componente padre (misma forma que antes).
        // El rango de fechas se deriva del mes (1º al último día).
        this.$emit('created', {
          id: data.data ? data.data.id : Date.now(),
          order_id: order0.id,
          order_code: this.form.orders.map(o => o.code).join(', '),
          contractor_id: order0.partner_id,
          contractor_name: order0.partner_name || '',
          certifies_by: order0.certifies_by,
          month: moment(this.form.month).format('MM-YYYY'),
          date_from: moment(this.form.month).startOf('month').format('YYYY-MM-DD'),
          date_to: moment(this.form.month).endOf('month').format('YYYY-MM-DD'),
          chargebacks: this.chargebacks.slice(),
          amount,
          observations: this.form.observations,
          is_last: this.form.isLast,
        })
        this.$emit('close')
      } catch (e) {
        this._NotifyApiError(e)
      } finally {
        this._ClearProgressTimers()
        this.isCreating = false
        this.progressStepKey = 'cert_progress_saving'
      }
    },
  },
}
</script>

<style lang="scss">
/* Estilos de contenido del formulario (sin chrome de modal: el contenedor externo
   aporta dialog/header/body/footer si aplica). */
.gen-cert-form {
  position: relative; /* ancla el overlay de b-loading */
  min-height: 4rem;
}
/* Compacta la separación label→control (Buefy/Bulma dejan mucho hueco por defecto). */
.gen-cert-form .field:not(:last-child) {
  margin-bottom: 0.75rem;
}
.gen-cert-form .label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.2rem;
  line-height: 1.2;
}
.gen-cert-form .help {
  margin-top: 0.15rem;
  font-size: 0.68rem;
}
/* Compacta los multiselect (contratista / orden de pedido) para igualar los inputs is-small. */
.gen-cert-form .multiselect,
.gen-cert-form .multiselect__tags {
  min-height: 32px;
}
.gen-cert-form .multiselect__tags {
  padding: 5px 34px 0 8px;
  font-size: 0.78rem;
}
.gen-cert-form .multiselect__single,
.gen-cert-form .multiselect__placeholder,
.gen-cert-form .multiselect__input {
  font-size: 0.78rem;
  line-height: 1.2;
  margin-bottom: 5px;
  padding-top: 0;
}
.gen-cert-form .multiselect__select {
  height: 32px;
}
/* Multiselect de líneas BOQ: item como tag + nombre a 2 líneas con elipsis
   (line-clamp no lo genera el Tailwind del proyecto). */
.gen-cert-boq__tag {
  flex-shrink: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.62rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
  border-radius: 4px;
  padding: 1px 5px;
}
.gen-cert-boq__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.62rem;
  color: #9ca3af;
}
.gen-cert-boq__name {
  font-size: 0.72rem;
  line-height: 1.25;
  color: #374151;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.gen-cert-boq__opt { white-space: normal; }
/* Opción del selector de orden de pedido: código a la izquierda + tag a la derecha. */
.gen-cert-order__opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}
.gen-cert-order__code {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Tag compacto de "ampliación" (ámbar) a la derecha del código. */
.gen-cert-ext-tag {
  flex-shrink: 0;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #92400e;
  background: #fef3c7;
  border-radius: 4px;
  padding: 1px 6px;
}
.gen-cert-boq__ref { font-size: 0.72rem; }
.gen-cert-boq__meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  font-size: 0.66rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
/* Overlay de generación (cubre el cuerpo del formulario) */
.gen-cert-form .loading-overlay {
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(1.5px);
}
.gen-cert-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  text-align: center;
  padding: 1rem 1.5rem;
}
.gen-cert-loader__ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #dbeafe;
  border-top-color: #2563eb;
  animation: gen-cert-spin 0.8s linear infinite;
}
@keyframes gen-cert-spin { to { transform: rotate(360deg); } }
.gen-cert-loader__step {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e3a8a;
}
.gen-cert-loader__hint {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}
.multiselect--danger .multiselect__tags {
  border-color: #e3342f !important;
}
.gen-cert-dialog__field {
  margin-bottom: 0.55rem !important;
}
.gen-cert-dialog__field--half {
  flex: 1;
  margin-bottom: 0 !important;
}
/* Fieldset del formulario de contracargo: borde azul claro con leyenda (look pro) */
.gen-cert-dialog__fieldset {
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 0.6rem 0.85rem 0.4rem;
  margin: 0 0 0.35rem;
  background: #f8fbff;
}
.gen-cert-dialog__fieldset > legend {
  width: auto;
  padding: 0 0.4rem;
  margin-left: 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #1d4ed8;
}

.gen-cert-dialog__table {
  width: 100%;
  margin-bottom: 0.55rem;
  border-collapse: collapse;
  font-size: 0.75rem;
  border: 1px solid #e5e7eb;
}
.gen-cert-dialog__table th,
.gen-cert-dialog__table td {
  padding: 0.35rem 0.45rem;
  border-top: 1px solid #f3f4f6;
}
.gen-cert-dialog__table thead th {
  background: #f3f4f6;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-top: 0;
}
.gen-cert-dialog__cb-obs {
  margin-top: 2px;
  font-style: italic;
  font-size: 0.68rem;
  color: #6b7280;
  line-height: 1.25;
}
.gen-cert-dialog__table .is-right { text-align: right; }
.gen-cert-dialog__table .is-center { text-align: center; width: 90px; }
.gen-cert-dialog__table .is-empty {
  text-align: center;
  color: #6b7280;
  padding: 0.65rem;
}
.gen-cert-dialog__btn {
  min-width: 7rem;
  height: 2.15rem;
  padding: 0 0.9rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease, opacity 150ms ease;
}
.gen-cert-dialog__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.gen-cert-dialog__btn--sm {
  min-width: auto;
  height: 2rem;
  white-space: nowrap;
  flex-shrink: 0;
}
.gen-cert-dialog__btn--cancel {
  background: #e5e7eb;
  color: #1f2937;
}
.gen-cert-dialog__btn--cancel:hover:not(:disabled) {
  background: #d1d5db;
}
.gen-cert-dialog__btn--confirm {
  background: #5577BB;
  color: #fff;
  box-shadow: 0 1px 2px rgba(28, 69, 149, 0.25);
}
.gen-cert-dialog__btn--confirm:hover:not(:disabled) {
  background: #385DA5;
}
.action-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
}
.action-icon-btn:hover {
  background: #e5e7eb;
  color: #111827;
}
.action-icon-btn--delete {
  color: #dc2626;
}
.action-icon-btn--delete:hover {
  background: #fee2e2;
  color: #b91c1c;
}
</style>
